/**
 * Month arithmetic, in one place.
 *
 * These helpers lived in both Journal and Budget, which is how two screens end
 * up disagreeing about what "last month" means. All dates are handled in UTC:
 * a month boundary computed in local time drifts by a day for anyone east or
 * west of the server, and spending on the 1st would land in the wrong month.
 */
export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function shiftMonth(month: string, delta: number): string {
  const [yearText = '1970', monthText = '01'] = month.split('-');
  const date = new Date(Date.UTC(Number(yearText), Number(monthText) - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function monthLabel(month: string): string {
  const [yearText = '1970', monthText = '01'] = month.split('-');
  const date = new Date(Date.UTC(Number(yearText), Number(monthText) - 1, 1));
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** First and last calendar day of the month, for date-range queries. */
export function monthRange(month: string): { from: string; to: string } {
  const [yearText = '1970', monthText = '01'] = month.split('-');
  const last = new Date(Date.UTC(Number(yearText), Number(monthText), 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

export function dayLabel(iso: string): string {
  const [y = '1970', m = '01', d = '01'] = iso.split('-');
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** Today, or the 1st of `month` when browsing a different one. */
export function defaultDateFor(month: string): string {
  if (month === currentMonth()) {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }
  return `${month}-01`;
}
